import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { ImagesEditor } from "../features/images/components/ImagesEditor";
import { LinksEditor } from "../features/links/components/LinksEditor";
import { venueQueries } from "../features/venues/api/queries";
import { AppHeader } from "../shared/ui/AppHeader";

export const Route = createFileRoute("/_authenticated/venues/$venueId/")({
  loader: async ({ params, context: { queryClient } }) => {
    const data = await queryClient.ensureQueryData(
      venueQueries.detail(params.venueId),
    );
    if (!data.venue) throw notFound();
  },
  component: VenueDetailPage,
  errorComponent: ({ error }) => (
    <>
      <AppHeader />
      <main className="mx-auto max-w-2xl p-6">
        <p role="alert" className="text-brand">
          Couldn’t load this venue: {error.message}
        </p>
      </main>
    </>
  ),
  notFoundComponent: () => (
    <>
      <AppHeader />
      <main className="mx-auto max-w-2xl p-6">
        <h1 className="text-xl font-semibold">Venue not found</h1>
        <p className="mt-2 text-sm text-content-muted">
          It may have been removed, or the link may be wrong.
        </p>
        <Link
          to="/venues"
          className="mt-4 inline-block text-sm text-brand underline"
        >
          Back to venues
        </Link>
      </main>
    </>
  ),
});

/**
 * One venue, with its rooms and everything attached to it.
 *
 * Read-mostly, like the organiser detail page: name, address, area and rooms are
 * edited by the dialog at `/venues/$venueId/edit`, and duplicating that form here
 * would mean two forms writing one row. What this page adds is somewhere for the
 * rooms to be *seen* — a list row has space for a name and an address, not for
 * the names a venue also answers to — and a stable URL per venue.
 *
 * The links and images editors are here for the same reason they are on the
 * organiser page: both attach to a row that already exists and both save on
 * their own.
 */
function VenueDetailPage() {
  const { venueId } = Route.useParams();
  const { data } = useSuspenseQuery(venueQueries.detail(venueId));

  const venue = data.venue;
  // The loader guarantees this; the type does not.
  if (!venue) return null;

  const cover =
    venue.images.find((image) => image.isPrimary) ?? venue.images[0];

  return (
    <>
      <AppHeader />
      <main className="mx-auto max-w-2xl p-6">
        <Link
          to="/venues"
          className="text-sm text-content-muted hover:text-content"
        >
          ← All venues
        </Link>

        <div className="mt-4 flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-4">
            {cover && (
              <img
                src={cover.thumbnailUrl}
                alt={cover.altText ?? ""}
                className="h-20 w-20 shrink-0 rounded-lg object-cover"
              />
            )}
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold">
                {venue.name}
                {venue.isOutdoor && (
                  <span className="ml-2 rounded-full bg-surface-muted px-2 py-0.5 align-middle text-xs font-normal text-content-muted">
                    Outdoor
                  </span>
                )}
              </h1>
              {venue.neighbourhood && (
                <p className="text-sm text-content-muted">
                  {venue.neighbourhood}
                </p>
              )}
            </div>
          </div>

          <Link
            to="/venues/$venueId/edit"
            params={{ venueId: venue.id }}
            className="shrink-0 rounded-md border border-border-subtle px-3 py-1 text-sm hover:bg-surface-muted"
          >
            Edit
          </Link>
        </div>

        <dl className="mt-6 grid gap-x-6 gap-y-3 sm:grid-cols-[8rem_1fr]">
          {venue.addressLine && (
            <>
              <dt className="text-sm font-medium text-content-muted">
                Address
              </dt>
              <dd>{venue.addressLine}</dd>
            </>
          )}

          <dt className="text-sm font-medium text-content-muted">Weather</dt>
          <dd>
            {venue.area ? (
              venue.area.name
            ) : (
              <>
                No area set
                {/*
                  Worth spelling out: a missing area is not cosmetic, it is why
                  the event cards for this venue show no forecast at all.
                */}
                <span className="block text-sm text-content-muted">
                  Events here show no forecast until one is set.
                </span>
              </>
            )}
          </dd>
        </dl>

        <section className="mt-6 rounded-lg border border-border-subtle p-4">
          <h2 className="font-medium">Rooms</h2>
          {venue.rooms.length > 0 ? (
            <>
              <ul className="mt-3 flex flex-wrap gap-2">
                {venue.rooms.map((room) => (
                  <li
                    key={room}
                    className="rounded-full bg-surface-muted px-3 py-1 text-sm"
                  >
                    {room}
                  </li>
                ))}
              </ul>
              {/*
                The point of the section, and not obvious from a list of names:
                these are matching input, not places. An event that names one
                still belongs to this venue and gets this venue's forecast.
              */}
              <p className="mt-3 text-sm text-content-muted">
                A calendar naming one of these finds this venue, and the event
                gets this venue’s address and forecast.
              </p>
            </>
          ) : (
            <p className="mt-2 text-sm text-content-muted">
              None listed. Add one if a calendar names a room here rather than
              the building — otherwise those events arrive with no venue at all.
            </p>
          )}
        </section>

        {/*
          Both save on their own and invalidate the venue queries, so this page
          picks up their changes without a form around them.
        */}
        <div className="mt-6 flex flex-col gap-4">
          <LinksEditor owner="venue" ownerId={venue.id} links={venue.links} />
          <ImagesEditor
            owner="venue"
            ownerId={venue.id}
            images={venue.images}
          />
        </div>
      </main>
    </>
  );
}
